import type { Schemas } from '@npc/api-client'
import { useQueries } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Globe2, Rocket, ScanSearch } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import './release-center.css'
import type { FastDelivery, FastDeliveryProduction } from '../../app/fast-deliveries'
import { listProjectFastDeliveries, projectFastDeliveriesQueryKey } from '../../app/fast-deliveries'
import { monthDayTime } from '../../app/format'
import { useLiveActivity } from '../../app/live-events'
import { screenLabel } from '../../app/nav'
import {
  useContinueInspectionRepair,
  useInspectionFindings,
  type InspectionFinding,
} from '../../app/inspection-findings'
import {
  groupInspectionFindings,
  inspectionEmptyKind,
  inspectionFindingSummary,
  inspectionGroupCountLabel,
  inspectionGroupNewest,
  inspectionRepairTaskId,
} from './empty'
import {
  deliveryLaneKind,
  isInfraFastReleaseId,
  isVerifiedProductionDelivery,
} from '../project/modules/fast-delivery-projection'
import { useProjects } from '../../app/queries'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { cn } from '../../components/cn'
import { Page, PageHeader } from '../../components/page'
import { ScreenError, ScreenLoading } from '../../components/state'
import { StatusDot, Tag, type TagTone } from '../../components/tag'
import { t } from '../../copy'
import { pj } from '../../copy/pjshell'
import { PJ } from '../../copy/pjshell-text'
import { T } from '../../copy/text'
import { fastDeliveryStatus } from '../project/modules/releases'
import {
  deliveryActivityLane,
  deliveryReceipt,
  failureFile,
  mainMatchesProduction,
  currentMainShaFromDeliveries,
  visibleAffectedServices,
} from '../project/modules/fast-delivery-projection'
import { StaticPublishWorkspace } from '../project/static-sites'

type ReleaseTab = 'applications' | 'sites' | 'inspections'

type NamedDelivery = {
  project: Schemas['Project']
  delivery: FastDelivery
  production: FastDeliveryProduction | null
}

type NamedProduction = {
  project: Schemas['Project']
  production: FastDeliveryProduction
}

const TABS: Array<{
  id: ReleaseTab
  copyId: 'code.tab.applications' | 'code.tab.sites' | 'code.tab.inspections'
  icon: typeof Rocket
}> = [
  { id: 'applications', copyId: 'code.tab.applications', icon: Rocket },
  { id: 'sites', copyId: 'code.tab.sites', icon: Globe2 },
  { id: 'inspections', copyId: 'code.tab.inspections', icon: ScanSearch },
]

/** 账户级发布中心。应用、网页和巡检共享入口，但各自只读自己的真实数据源。 */
export function CodeScreen() {
  const [tab, setTab] = useState<ReleaseTab>('applications')

  return (
    <Page className="release-center-theme" width="workspace">
      <PageHeader
        title={screenLabel('code')}
        subtitle={<T id="code.page.subtitle" />}
        className="mb-npc-grid"
      />

      <ReleaseTabs value={tab} onChange={setTab} />

      <div role="tabpanel" aria-labelledby={`release-tab-${tab}`} className="mt-npc-grid">
        {tab === 'applications' ? <ApplicationCenter /> : null}
        {tab === 'sites' ? (
          <StaticPublishWorkspace scope={{ kind: 'account' }} surface="paper" />
        ) : null}
        {tab === 'inspections' ? <InspectionCenter /> : null}
      </div>
    </Page>
  )
}

function ReleaseTabs({
  value,
  onChange,
}: {
  value: ReleaseTab
  onChange: (tab: ReleaseTab) => void
}) {
  return (
    <div
      role="tablist"
      aria-label={screenLabel('code')}
      className="flex min-h-11 w-fit max-w-full gap-1 overflow-x-auto border-b border-npc-border-soft"
    >
      {TABS.map(({ id, copyId, icon: Icon }) => (
        <button
          key={id}
          id={`release-tab-${id}`}
          type="button"
          role="tab"
          aria-selected={value === id}
          onClick={() => onChange(id)}
          className={cn(
            'relative inline-flex min-h-11 shrink-0 items-center gap-npc-gap-xs border-0 bg-transparent px-npc-grid-sm text-npc-body-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--rc-accent)]',
            value === id ? 'font-semibold text-[color:var(--rc-accent)]' : 'text-npc-ink-soft hover:text-npc-ink',
          )}
        >
          <Icon aria-hidden="true" size={16} />
          <T id={copyId} />
          {value === id ? <span className="absolute inset-x-0 bottom-0 h-[3px] bg-[color:var(--rc-accent)]" /> : null}
        </button>
      ))}
    </div>
  )
}

function ApplicationCenter() {
  const projects = useProjects()
  const live = useLiveActivity()
  const projectItems = projects.data?.items ?? []
  const releaseQueries = useQueries({
    queries: projectItems.map((project) => ({
      queryKey: projectFastDeliveriesQueryKey(project.id),
      queryFn: () => listProjectFastDeliveries(project.id),
      staleTime: 15_000,
    })),
  })

  const { productions, deliveries } = useMemo(() => {
    const nextProductions: NamedProduction[] = []
    const nextDeliveries: NamedDelivery[] = []
    projectItems.forEach((project, index) => {
      const data = releaseQueries[index]?.data
      if (data?.current_production_baseline) {
        nextProductions.push({ project, production: data.current_production_baseline })
      }
      for (const delivery of data?.deliveries ?? []) {
        const baseline = data?.current_production_baseline ?? null
        nextDeliveries.push({
          project,
          delivery,
          production: baseline && baseline.repo === delivery.repo ? baseline : null,
        })
      }
    })
    nextDeliveries.sort(
      (a, b) => Date.parse(b.delivery.updated_at) - Date.parse(a.delivery.updated_at),
    )
    nextProductions.sort((a, b) => a.project.name.localeCompare(b.project.name, 'zh-CN'))
    return { productions: nextProductions, deliveries: nextDeliveries }
  }, [projectItems, releaseQueries])

  if (projects.isPending || releaseQueries.some((query) => query.isPending)) {
    return <ScreenLoading />
  }
  if (projects.error) return <ScreenError error={projects.error} />

  const attention = deliveries.filter(
    ({ delivery, production }) => deliveryActivityLane(delivery, { production }) === 'attention',
  )
  const active = deliveries.filter(
    ({ delivery, production }) => deliveryActivityLane(delivery, { production }) === 'active',
  )
  const partial = releaseQueries.some((query) => query.isError)

  return (
    <div data-testid="release-center-applications">
      {live.connectionStatus === 'disconnected' ? (
        <Notice>
          <PJ id="pjshell.release.connection_lost.body" />
        </Notice>
      ) : null}
      {partial ? (
        <Notice>
          <T id="code.application.partial" />
        </Notice>
      ) : null}

      <ReleasePulse live={productions.length} active={active.length} attention={attention.length} />

      <MainVsProduction productions={productions} deliveries={deliveries} />

      {attention.length > 0 ? (
        <ReleaseSection title={<T id="code.overview.attention" />} tone="danger">
          <DeliveryRows items={attention} />
        </ReleaseSection>
      ) : null}

      {active.length > 0 ? (
        <ReleaseSection title={<PJ id="pjshell.release.lane.fast_candidates" />} tone="active">
          <DeliveryRows items={active} />
        </ReleaseSection>
      ) : null}

      <ReleaseSection title={<PJ id="pjshell.release.lane.verified_production" />}>
        {productions.length > 0 ? <ProductionRows items={productions} /> : <EmptyLine />}
      </ReleaseSection>

      <ReleaseSection title={<T id="code.application.recent" />}>
        {deliveries.length > 0 ? <DeliveryRows items={deliveries.slice(0, 20)} /> : <EmptyLine />}
      </ReleaseSection>
    </div>
  )
}

function MainVsProduction({
  productions,
  deliveries,
}: {
  productions: NamedProduction[]
  deliveries: NamedDelivery[]
}) {
  if (productions.length === 0) return null
  return (
    <section
      data-testid="release-center-main-vs-production"
      className="mt-npc-grid min-w-0 overflow-hidden border-y border-npc-border-soft"
    >
      {productions.map(({ project, production }, index) => {
        const mainSha = currentMainShaFromDeliveries(
          deliveries.filter((item) => item.project.id === project.id).map((item) => item.delivery),
        )
        const comparison = mainMatchesProduction(mainSha, production.sha)
        return (
          <div
            key={`${project.id}-${production.release_id}`}
            className={cn(
              'grid min-w-0 grid-cols-1 gap-npc-gap-sm px-npc-grid-sm py-npc-gap-sm min-[700px]:grid-cols-3',
              index > 0 && 'border-t border-npc-border-softer',
            )}
          >
            <div className="min-w-0">
              <div className="text-npc-caption text-npc-ink-soft">
                <PJ id="pjshell.release.current_main" />
              </div>
              <div className="mt-0.5 min-w-0 truncate">
                {mainSha ? <Commit value={mainSha} /> : <PJ id="pjshell.release.main_vs_production.unknown" />}
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-npc-caption text-npc-ink-soft">
                <PJ id="pjshell.release.production" />
              </div>
              <div className="mt-0.5 min-w-0 truncate">
                <Commit value={production.sha} />
              </div>
              {production.receipt_digest ? (
                <div
                  className="mt-0.5 min-w-0 truncate font-npc-mono text-npc-caption-sm text-npc-ink-softer"
                  title={production.receipt_digest}
                >
                  <PJ id="pjshell.release.receipt" /> {production.receipt_digest.slice(0, 8)}
                </div>
              ) : null}
              {production.previous_sha ? (
                <div className="mt-0.5 min-w-0 truncate text-npc-caption-sm text-npc-ink-softer">
                  <PJ id="pjshell.release.rollback_point" /> <Commit value={production.previous_sha} />
                </div>
              ) : null}
            </div>
            <div className="min-w-0">
              <div className="text-npc-caption text-npc-ink-soft">
                <PJ id="pjshell.release.main_vs_production" />
              </div>
              <div className="mt-0.5">
                {comparison === 'match' ? (
                  <Tag tone="ok">
                    <PJ id="pjshell.release.main_vs_production.match" />
                  </Tag>
                ) : comparison === 'mismatch' ? (
                  <Tag tone="warn">
                    <PJ id="pjshell.release.main_vs_production.mismatch" />
                  </Tag>
                ) : (
                  <PJ id="pjshell.release.main_vs_production.unknown" />
                )}
              </div>
            </div>
          </div>
        )
      })}
    </section>
  )
}

function ReleasePulse({
  live,
  active,
  attention,
}: {
  live: number
  active: number
  attention: number
}) {
  const facts = [
    { id: 'code.overview.live' as const, value: live, tone: 'ok' as const },
    { id: 'code.overview.active' as const, value: active, tone: 'info' as const },
    {
      id: 'code.overview.attention' as const,
      value: attention,
      tone: attention ? ('danger' as const) : ('neutral' as const),
    },
  ]
  return (
    <section
      data-testid="release-center-summary"
      className="grid grid-cols-3 border-y border-npc-border-soft bg-npc-surface/45 max-[600px]:grid-cols-1"
    >
      {facts.map((fact, index) => (
        <div
          key={fact.id}
          className={cn(
            'flex min-h-20 items-center gap-npc-gap-sm px-npc-grid-sm py-npc-gap-sm',
            index > 0 &&
              'border-l border-npc-border-soft max-[600px]:border-l-0 max-[600px]:border-t',
          )}
        >
          <StatusDot
            tone={fact.tone}
            pulsing={fact.id === 'code.overview.active' && fact.value > 0}
          />
          <div>
            <div className="text-npc-caption text-npc-ink-soft">
              <T id={fact.id} />
            </div>
            <div className="mt-0.5 text-npc-h1-sm font-semibold text-npc-ink">{fact.value}</div>
          </div>
        </div>
      ))}
    </section>
  )
}

function ReleaseSection({
  title,
  children,
  tone = 'default',
}: {
  title: ReactNode
  children: ReactNode
  tone?: 'default' | 'active' | 'danger'
}) {
  return (
    <section className="mt-npc-modal" data-tone={tone}>
      <div className="mb-npc-gap-sm flex items-center gap-npc-gap-xs">
        {tone !== 'default' ? (
          <StatusDot tone={tone === 'danger' ? 'danger' : 'info'} pulsing={tone === 'active'} />
        ) : null}
        <h2 className="m-0 text-npc-section font-semibold text-npc-ink">{title}</h2>
      </div>
      <div className="overflow-hidden border-y border-npc-border-soft">{children}</div>
    </section>
  )
}

function ProductionRows({ items }: { items: NamedProduction[] }) {
  return (
    <div>
      {items.map(({ project, production }, index) => (
        <Link
          key={`${project.id}-${production.release_id}`}
          to="/projects/$projectId/releases"
          params={{ projectId: project.id }}
          className={cn(
            'grid min-h-16 grid-cols-[minmax(150px,0.8fr)_minmax(200px,1.3fr)_auto] items-center gap-npc-grid-sm px-npc-grid-sm py-npc-gap-sm text-inherit no-underline transition-colors hover:bg-npc-surface max-[700px]:grid-cols-1 max-[700px]:gap-npc-gap-xs',
            index > 0 && 'border-t border-npc-border-softer',
          )}
        >
          <div className="min-w-0">
            <div className="truncate text-npc-body-sm font-medium text-npc-ink">{project.name}</div>
            <div className="mt-0.5 truncate text-npc-caption-sm text-npc-ink-soft">
              {production.repo}
            </div>
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-npc-gap-xs">
              <Tag tone="ok">
                <PJ id="pjshell.release.badge.verified_production" />
              </Tag>
              <Tag tone="ok">
                <PJ id="pjshell.release.state.production_verified" />
              </Tag>
              <Commit value={production.sha} />
            </div>
            <div className="mt-npc-gap-xs truncate text-npc-caption-sm text-npc-ink-soft">
              {servicesText(production.services)}
            </div>
          </div>
          <time
            className="text-npc-caption-sm text-npc-ink-softer"
            dateTime={production.verified_at}
          >
            {monthDayTime(production.verified_at)}
          </time>
        </Link>
      ))}
    </div>
  )
}

function DeliveryRows({ items }: { items: NamedDelivery[] }) {
  return (
    <div>
      {items.map(({ project, delivery }, index) => {
        const status = fastDeliveryStatus(delivery)
        return (
          <Link
            key={`${project.id}-${delivery.release_id}`}
            to="/projects/$projectId/releases"
            params={{ projectId: project.id }}
            aria-label={`${t('code.application.open_project')} ${project.name}`}
            className={cn(
              'grid min-h-20 grid-cols-[minmax(150px,0.72fr)_minmax(260px,1.5fr)_auto] items-center gap-npc-grid-sm px-npc-grid-sm py-npc-gap-sm text-inherit no-underline transition-colors hover:bg-npc-surface max-[700px]:grid-cols-1 max-[700px]:gap-npc-gap-xs',
              index > 0 && 'border-t border-npc-border-softer',
            )}
          >
            <div className="min-w-0">
              <div className="truncate text-npc-body-sm font-medium text-npc-ink">
                {project.name}
              </div>
              <div className="mt-0.5 truncate text-npc-caption-sm text-npc-ink-softer">
                {delivery.repo}
              </div>
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-npc-gap-xs">
                <Tag tone={tagTone(status.tone)}>
                  <PJ id={status.copyId} />
                </Tag>
                <DeliveryLaneBadges delivery={delivery} />
                <Commit value={delivery.candidate_sha} />
              </div>
              <div className="mt-npc-gap-xs min-w-0 break-all text-npc-caption text-npc-ink-soft">
                {delivery.status_message || servicesText(delivery.affected_services)}
              </div>
              <div className="mt-npc-gap-xs grid min-w-0 grid-cols-1 gap-npc-gap-xs text-npc-caption-sm text-npc-ink-softer sm:grid-cols-2">
                <span className="min-w-0 truncate font-npc-mono" title={delivery.release_id}>
                  <PJ id="pjshell.release.id" /> {delivery.release_id.slice(0, 8)}
                </span>
                {delivery.deployment_sha ? (
                  <span className="min-w-0">
                    <PJ id="pjshell.release.deployment" /> <Commit value={delivery.deployment_sha} />
                  </span>
                ) : null}
                {deliveryReceipt(delivery) ? (
                  <span className="min-w-0 truncate font-npc-mono" title={deliveryReceipt(delivery) ?? ''}>
                    <PJ id="pjshell.release.receipt" /> {(deliveryReceipt(delivery) ?? '').slice(0, 8)}
                  </span>
                ) : null}
                {failureFile(delivery) ? (
                  <span className="min-w-0 break-all">{failureFile(delivery)}</span>
                ) : null}
              </div>
            </div>
            <time
              className="text-npc-caption-sm text-npc-ink-softer"
              dateTime={delivery.updated_at}
            >
              {monthDayTime(delivery.updated_at)}
            </time>
          </Link>
        )
      })}
    </div>
  )
}

function InspectionCenter() {
  const findings = useInspectionFindings()
  const repair = useContinueInspectionRepair()

  if (findings.isPending) return <ScreenLoading shape="list" />
  if (findings.error) return <ScreenError error={findings.error} />

  const items = findings.data?.items ?? []
  const emptyKind = inspectionEmptyKind(items.length, findings.data?.last_completed_at)
  const groups = groupInspectionFindings(items)
  return (
    <Card padding="none" className="overflow-hidden" data-testid="code-inspections">
      <div className="border-b border-npc-border-soft px-npc-card pb-npc-grid-sm pt-npc-card-sm">
        <CardTitle>
          <T id="code.tab.inspections" />
        </CardTitle>
        <CardHint className="mt-[3px]">
          <T id="code.inspection.subtitle" />
        </CardHint>
      </div>
      {emptyKind === 'idle' ? (
        <p
          className="m-0 px-npc-card py-npc-card-sm text-npc-body-sm text-npc-ink-soft"
          data-testid="code-inspections-idle"
        >
          <T id="code.inspection.idle" />
        </p>
      ) : emptyKind === 'clean' ? (
        <p
          className="m-0 px-npc-card py-npc-card-sm text-npc-body-sm text-npc-ink-soft"
          data-testid="code-inspections-empty"
        >
          <T id="code.inspection.empty" />
        </p>
      ) : (
        groups.map((group, index) => (
          <InspectionFindingGroup
            key={inspectionFindingGroupKey(group, index)}
            group={group}
            index={index}
            repair={repair}
          />
        ))
      )}
    </Card>
  )
}

function inspectionFindingGroupKey(group: InspectionFinding[], index: number): string {
  return inspectionGroupNewest(group).id || `finding-group-${index}`
}

function InspectionFindingGroup({
  group,
  index,
  repair,
}: {
  group: InspectionFinding[]
  index: number
  repair: { isPending: boolean; mutate: (taskId: string) => void }
}) {
  const [open, setOpen] = useState(false)
  const newest = inspectionGroupNewest(group)
  const count = group.length
  const grouped = count > 1
  const rest = grouped ? group.filter((item) => item !== newest) : []
  const repairTaskId = inspectionRepairTaskId(newest)
  const title = inspectionFindingSummary(newest)
  return (
    <div
      data-testid={grouped ? 'code-inspections-group' : undefined}
      className={
        index > 0
          ? 'border-t border-npc-border-softer px-npc-card py-npc-grid-sm'
          : 'px-npc-card py-npc-grid-sm'
      }
    >
      <div className="flex flex-wrap items-center gap-npc-gap-xs">
        {newest.open ? (
          <Tag tone="danger">
            <T id="code.overview.attention" />
          </Tag>
        ) : null}
        <div
          className="min-w-0 flex-1 text-npc-body-sm font-medium text-npc-ink"
          data-testid="code-inspections-summary"
        >
          {title}
        </div>
        {grouped ? (
          <button
            type="button"
            data-testid="code-inspections-group-count"
            className="shrink-0 rounded-npc-chip-inline border-0 bg-npc-surface px-[7px] py-0.5 text-npc-caption text-npc-ink-soft"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            {inspectionGroupCountLabel(count)}
          </button>
        ) : null}
        {repairTaskId ? (
          <Button
            size="sm"
            data-testid={`inspection-repair-${newest.id}`}
            disabled={repair.isPending}
            onClick={() => repair.mutate(repairTaskId)}
          >
            <T id="code.inspection.repair" />
          </Button>
        ) : null}
      </div>
      <ShaPair base={newest.base_sha} candidate={newest.candidate_sha} />
      <details className="mt-npc-gap-xs" data-testid="code-inspections-tech">
        <summary className="cursor-pointer text-npc-caption text-npc-ink-soft">
          <PJ id="pjshell.release.technical" />
        </summary>
        <div className="mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          {newest.rule ? <p className="m-0">rule: {newest.rule}</p> : null}
          {newest.evidence ? <p className="m-0 mt-npc-gap-xs">{newest.evidence}</p> : null}
        </div>
      </details>
      {newest.changed_paths && newest.changed_paths.length > 0 ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          {newest.changed_paths.join('、')}
        </p>
      ) : null}
      {newest.call_receipt ? (
        <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
          {newest.call_receipt.provider} {newest.call_receipt.requested_model}
          {newest.call_receipt.actual_model &&
          newest.call_receipt.actual_model !== newest.call_receipt.requested_model
            ? ` → ${newest.call_receipt.actual_model}`
            : ''}
          {newest.call_receipt.call_id ? ` ${newest.call_receipt.call_id}` : ''}
          {` ${newest.call_receipt.prompt_tokens}+${newest.call_receipt.output_tokens}`}
        </p>
      ) : null}
      {grouped && open ? (
        <ul
          className="m-0 mt-npc-gap-xs list-none p-0"
          data-testid="code-inspections-group-members"
        >
          {rest.map((item, memberIndex) => (
            <li
              key={item.id || `member-${memberIndex}`}
              className="mt-npc-gap-xs text-npc-caption text-npc-ink-soft"
            >
              <ShaPair base={item.base_sha} candidate={item.candidate_sha} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

function ShaPair({ base, candidate }: { base?: string; candidate?: string }) {
  if (!base && !candidate) return null
  return (
    <p className="m-0 mt-npc-gap-xs text-npc-caption text-npc-ink-soft">
      {base ? <Commit value={base} /> : null}
      {base && candidate ? ' → ' : null}
      {candidate ? <Commit value={candidate} /> : null}
    </p>
  )
}

function Notice({ children }: { children: ReactNode }) {
  return (
    <div className="mb-npc-grid border-l-2 border-npc-amber bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink">
      {children}
    </div>
  )
}

function EmptyLine() {
  return (
    <div className="px-npc-grid-sm py-npc-card-sm text-npc-body-sm text-npc-ink-soft">
      <T id="code.application.empty" />
    </div>
  )
}

function Commit({ value }: { value: string }) {
  return (
    <code className="font-npc-mono text-npc-caption text-npc-ink-medium" title={value}>
      {value.slice(0, 8)}
    </code>
  )
}

function servicesText(services: string[]): string {
  const visible = visibleAffectedServices(services)
  return visible.length > 0 ? visible.join('、') : pj('pjshell.release.services.none')
}

function DeliveryLaneBadges({ delivery }: { delivery: FastDelivery }) {
  if (isVerifiedProductionDelivery(delivery)) {
    return (
      <Tag tone="ok" data-testid="release-lane-verified">
        <PJ id="pjshell.release.badge.verified_production" />
      </Tag>
    )
  }
  const synthetic = isInfraFastReleaseId(delivery.deployment_release_id) ||
    isInfraFastReleaseId(delivery.release_id)
  const lane = deliveryLaneKind(delivery)
  if (lane !== 'fast_candidate' && !synthetic) return null
  return (
    <>
      <Tag tone="info" data-testid="release-lane-fast-candidate">
        <PJ id="pjshell.release.badge.fast_candidate" />
      </Tag>
      {synthetic ? (
        <Tag tone="neutral" data-testid="release-lane-infra-fast">
          <PJ id="pjshell.release.badge.infra_fast" />
        </Tag>
      ) : null}
    </>
  )
}

function tagTone(tone: 'ok' | 'warn' | 'bad' | 'neutral'): TagTone {
  if (tone === 'bad') return 'danger'
  if (tone === 'ok') return 'ok'
  if (tone === 'warn') return 'info'
  return 'neutral'
}
